import type { ApiPromise } from '@polkadot/api'
import type { Signer } from '@polkadot/types/types'
import type { QueryClient } from '@tanstack/query-core'
import type {
  MachineConfig,
  AnyEventObject,
  InternalMachineOptions,
  EventObject,
  ResolveTypegenMeta,
  BaseActionObject,
  ServiceMap,
  TypegenConstraint,
  TypegenDisabled,
  StateMachine,
  State,
} from 'xstate'

import Decimal from 'decimal.js'
import { assign } from 'xstate'
import { mergeDeepRight } from 'ramda'

//
// Types
//

export type SpiritInfo = {
  account: string
  collectionId: number
  nftId: number
  formattedNftId: number
}

export type StatName =
  | 'dexterity'
  | 'intellect'
  | 'reputation'
  | 'strength'
  | 'willpower'

export interface Stat {
  current_level_exp_begin: number
  current_level_exp_end: number
  exp: number
  level: number
}

export interface SpiritState {
  extra: Record<string, unknown>
  stats: Record<StatName, Stat>
  stats_refreshed_at?: string
}

export interface ClaimSpiritContext {
  name: string
  address?: string
  linked?: Record<string, number>
  spirit?: SpiritState
  spiritCollection: SpiritInfo[]
  balance: Decimal
  hasInited: boolean
  //
  api: ApiPromise | undefined
  isConnected: boolean
  lastError?: string | undefined | null

  queryClient: QueryClient
  pwApiPrefix: string
  mcpPrefix: string

  //
  canClaimSpirits: boolean
  //
  invited?: boolean
  is_whitelisted?: boolean

  mcpSign?: string
  signer?: Signer

  refCode?: string
}

export type ClaimSpiritMachine = StateMachine<ClaimSpiritContext, any, AnyEventObject>

export type ClaimSpiritState = State<ClaimSpiritContext, AnyEventObject, any, {
  value: any
  context: ClaimSpiritContext
},
  TypegenDisabled
>

export interface ClaimSpiritMachineServiceMap extends ServiceMap {
  checkState: {
    data: (context: ClaimSpiritContext) => Promise<unknown>
  }
  createSpirit: {
    data: (context: ClaimSpiritContext) => Promise<unknown>
  }
  updateSpirit: {
    data: (context: ClaimSpiritContext) => Promise<unknown>
  }
  claimSpirit: {
    data: (context: ClaimSpiritContext) => Promise<unknown>
  }
  removeSpirit: {
    data: (context: ClaimSpiritContext) => Promise<unknown>
  }
}

export type ClaimSpiritMachineInternalMachineOptions<
  TEvent extends EventObject = AnyEventObject,
  TTypesMeta extends TypegenConstraint = TypegenDisabled
> = InternalMachineOptions<
  ClaimSpiritContext,
  TEvent,
  ResolveTypegenMeta<
    TTypesMeta,
    TEvent,
    BaseActionObject,
    ClaimSpiritMachineServiceMap
  >
>

export type ClaimSpiritMachineOptionsOverrides =
  Partial<ClaimSpiritMachineInternalMachineOptions> &
    Pick<ClaimSpiritMachineInternalMachineOptions, 'services'>

//
// State Machine Factory Functions.
//

export function makeConfig(ctx: ClaimSpiritContext): MachineConfig<ClaimSpiritContext, any, AnyEventObject> {
  return {
    id: 'claim-spirit',
    initial: 'initial',
    context: ctx,
    predictableActionArguments: true,
    states: {
      initial: {
        always: [
          {target: 'anonymous', cond: 'hasNoSelectedWallet'},
          {target: 'disconnected', cond: 'isDisconnected'},
          {target: 'signedIn', cond: 'hasSelectedWallet'},
        ],
      },
      anonymous: {
        on: {
          updateDependencies: {
            target: 'initial',
            actions: ['updateDependencies'],
          },
        },
      },
      disconnected: {
        on: {
          updateDependencies: {
            target: 'initial',
            actions: ['updateDependencies'],
          },
        },
      },
      signedIn: {
        on: {
          updateDependencies: {
            target: 'initial',
            actions: ['updateDependencies'],
          },
          updateBalance: {
            internal: true,
            actions: ['updateBalance'],
          },
          setMcpSign: {
            internal: true,
            actions: ['setMcpSign'],
          },
        },

        initial: 'checkingState',

        states: {
          checkingState: {
            invoke: {
              id: 'checkState',
              src: 'checkState',
              onDone: {
                target: 'checked',
                actions: ['setState'],
              },
              onError: {
                target: 'walletConnected',
                actions: ['saveLastError'],
              },
            },
          },
          checked: {
            always: [
              {target: 'claimed', cond: 'hasClaimed'},
              {target: 'waiting', cond: 'isWaiting'},
              {target: 'closed', cond: 'isClosed'},
              {target: 'ready', cond: 'isReady'},
              {target: 'created', cond: 'hasCreated'},
              {target: 'linked', cond: 'hasLinked'},
              {target: 'walletConnected', cond: 'isNewbie'},
            ],
          },
          waiting: {
            on: {
              refresh: 'checkingState',
            },
          },
          walletConnected: {
            on: {
              generateIdentity: 'linking',
            },
          },
          linking: {
            on: {
              callbackSucceed: 'checkingState',
              callbackFailed: 'walletConnected',
              manualCheck: 'checkingState',
            },
          },
          linked: {
            on: {
              createSpirit: {
                target: 'creatingSpirit',
                cond: 'canCreateSpirit',
              },
            },
          },
          creatingSpirit: {
            invoke: {
              id: 'createSpirit',
              src: 'createSpirit',
              onDone: {
                target: 'created',
                actions: ['setSpirit'],
              },
              onError: {
                target: 'linked',
                actions: ['saveLastError'],
              },
            },
          },
          created: {
            on: {
              updateSpirit: 'updatingSpirit',
              removeSpirit: 'removingSpirit',
              refresh: 'checkingState',
            },
          },
          updatingSpirit: {
            invoke: {
              id: 'updateSpirit',
              src: 'updateSpirit',
              onDone: {
                target: 'checkingState',
                actions: ['setSpirit'],
              },
              onError: {
                target: 'checked',
                actions: ['saveLastError'],
              },
            },
          },
          closed: {
            on: {
              updateSpirit: 'updatingSpirit',
              removeSpirit: 'removingSpirit',
              refresh: 'checkingState',
            },
          },
          ready: {
            on: {
              updateSpirit: 'updatingSpirit',
              claimSpirit: 'claimingSpirit',
              removeSpirit: 'removingSpirit',
            },
          },
          claimed: {
            on: {
              updateSpirit: 'updatingSpirit',
              removeSpirit: 'removingSpirit',
            },
          },
          claimingSpirit: {
            invoke: {
              id: 'claimSpirit',
              src: 'claimSpirit',
              onDone: 'checkingState',
              onError: {
                target: 'checkingState',
                actions: ['saveLastError'],
              },
            },
          },
          removingSpirit: {
            invoke: {
              id: 'removeSpirit',
              src: 'removeSpirit',
              onDone: 'checkingState',
              onError: {
                target: 'checkingState',
                actions: ['saveLastError'],
              },
            },
          },
        },
      },
    },
  }
}

export function makeOptions(overrides: ClaimSpiritMachineOptionsOverrides): ClaimSpiritMachineInternalMachineOptions {
  return mergeDeepRight(
    {
      actions: {
        setState: assign({
          canClaimSpirits: (_, event) => !!event.data.canClaimSpirits,
          linked: (_, event) => event.data.linked,
          spirit: (_, event) => event.data.spirit,
          spiritCollection: (_, event) => event.data.spiritCollection,
          hasInited: (_, event) => true,
        }),
        setSpirit: assign({
          spirit: (_, event) => event.data,
        }),
        updateDependencies: assign({
          name: (_, {name}) => name,
          address: (_, {address}) => address,
          canClaimSpirits: (_) => false,
          linked: (_) => undefined,
          spirit: (_) => undefined,
          spiritCollection: (_) => [],
          api: (_, {api}) => api,
          isConnected: (_, {isConnected}) => isConnected,
          lastError: (_) => undefined,
          signer: (_, {signer}) => signer,
          refCode: (_, {refCode}) => refCode,
        }),
        updateBalance: assign({
          balance: (_, {balance}) => balance,
        }),
        setMcpSign: assign({
          mcpSign: (_, {mcpSign}) => mcpSign,
        }),
        saveLastError: assign({
          lastError: (_, {data}) => {
            console.error(data)
            return `${data.message}`
          },
        }),
      },
      guards: {
        isDisconnected: (context) => !context.api,
        hasNoSelectedWallet: (context) => !context.address,
        hasSelectedWallet: (context) => !!context.address,
        isNewbie: (context) => !context.linked && context.canClaimSpirits,
        hasLinked: (context) => !!context.linked,
        hasCreated: (context) => !!context.spirit && !!context.linked,
        isClosed: (context) => !context.canClaimSpirits,
        isWaiting: (context) => !context.canClaimSpirits && !context.linked,
        isReady: (context) =>
          !!context.spirit &&
          !!context.linked &&
          !!context.spirit.stats_refreshed_at &&
          context.spiritCollection.length === 0,
        hasClaimed: (context) =>
          !!context.spirit &&
          !!context.linked &&
          !!context.spirit.stats_refreshed_at &&
          (!context.spiritCollection || context.spiritCollection.length > 0),
        canCreateSpirit: (context) => context.balance.greaterThan('0.01'),
      },
    } as Partial<ClaimSpiritMachineInternalMachineOptions>,
    overrides,
  ) as ClaimSpiritMachineInternalMachineOptions
}
